import { RefObject } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { UploadFile } from 'antd';
import { UploadChangeParam } from 'antd/es/upload';

import { HideCondition, TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import { BaseDto } from '@/shared/lib/sj-orm/models/base.dto';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';
import { TimeDurationUnitsEnum } from '@/shared/lib/sj-orm/models/form/question-option.dto';
import { DeepPartial } from '@/shared/types/utility-types';

import { FileUploadingRef } from './components/FileUploading';
import { FieldsArrayRef } from './components/LegalizedCopiesField';
import { separator } from './constants';

type OmittedBaseDto = Omit<BaseDto, 'id'>;

export type UseFormRenderInputType<
  TDto extends OmittedBaseDto,
  THelp extends { [TKey in keyof TDto]: QuestionType },
> = {
  sectionItems?: TDtoWrapper<TDto, THelp>;
  form: UseFormReturn<TDto, unknown, undefined>;
  onSubmit?: (data: TDto) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultValues?: DeepPartial<TDto & any>;
  removeAllRequired?: boolean;
  validateOnMount?: boolean;
  uploaderRef?: RefObject<FileUploadingRef>;
  fieldsArrayRef?: RefObject<FieldsArrayRef>;
  showRequiredOnly?: boolean;
  mode?: 'save' | 'update';
  onHideFieldPredicate?: (condition: HideCondition) => boolean;
  highlightFieldsKeys?: Array<string>;
};

type DefaultInputType =
  | QuestionType.URL
  | QuestionType.TEXT
  | QuestionType.EMAIL
  | QuestionType.NUMBER
  | QuestionType.PHONE_NUMBER;

export const InputsWithPlaceholder = [
  QuestionType.URL,
  QuestionType.TEXT,
  QuestionType.NUMBER,
  QuestionType.EMAIL,
  QuestionType.PHONE_NUMBER,
];

export const isDefaultInput = (type: QuestionType): type is DefaultInputType =>
  InputsWithPlaceholder.includes(type);

export const DefaultInputToInputType: Record<DefaultInputType, string> = {
  [QuestionType.URL]: 'url',
  [QuestionType.TEXT]: 'text',
  [QuestionType.NUMBER]: 'number',
  [QuestionType.EMAIL]: 'email',
  [QuestionType.PHONE_NUMBER]: 'number',
};

export type KeyString<T = string> = { key: T };

export type FormItemKey = `${QuestionType}${typeof separator}${string}${typeof separator}${string}`;

export type ReturnedTypesDefault =
  | boolean
  | string
  | number
  | KeyString
  | KeyString<TimeDurationUnitsEnum>
  | Array<string>
  | UploadChangeParam<UploadFile>;

export type ReturnedTypes<T = ReturnedTypesDefault> = T;

export type FormItemsType = {
  [key: FormItemKey]: ReturnedTypes;
};
